import React from 'react';

import { c } from 'ttag';

import { Badge, Checkbox } from '@freelancelabs/components';

import { AccountActivitiesQuery } from '../../../graphql/__generated__/types';

type Activities = NonNullable<AccountActivitiesQuery['accountActivities']>;
type Option = Activities['needs'][number];

interface Props {
    activities: Activities;
    needIds: string[];
    serviceIds: string[];
    onToggleNeed: (id: string, checked: boolean) => void;
    onToggleService: (id: string, checked: boolean) => void;
    /** Coche ou décoche toutes les prestations d'un marché d'un seul geste. */
    onToggleMarket: (serviceIds: string[], checked: boolean) => void;
}

/**
 * LE SÉLECTEUR D'ACTIVITÉS de l'étape « Périmètre » (PCG-305).
 *
 * **On cochait des marchés.** La branche restreinte des vues joignait alors sur le marché, et
 * une activité qui n'en porte aucun ne correspondait à rien : elle sortait de la liste **en
 * silence**, 16 % des besoins et 27 % des prestations. On coche désormais les objets
 * eux-mêmes, et le refus qui protégeait de ce silence a disparu avec sa cause.
 *
 * **Attention, LE MARCHÉ N'EST QU'UNE CASE GROUPÉE.** Il regroupe et il coche en bloc, mais ce
 * que l'écran envoie est la liste des PRESTATIONS. Envoyer le marché ramènerait exactement le
 * défaut que ce lot corrige. C'est le dessin de la maquette du back-office, relevé dans son
 * code : sa case de cadre écrit dans `contractIds`, jamais dans une liste de cadres.
 *
 * **Le groupe « sans contrat cadre » se rend comme les autres**, rangé en dernier par l'API.
 * C'est près d'une prestation sur deux sur certains comptes, et c'est exactement ce que
 * l'ancien mécanisme perdait : l'écarter serait refaire le silence d'avant.
 *
 * ── LA FORME VIENT DE LA MAQUETTE, RELEVÉE DANS SON CSS
 *
 * `.picker` y est un bloc bordé qui porte **`max-height: 340px; overflow: auto`** : chaque
 * liste défile **dans elle-même** plutôt que d'allonger la page. C'est ce qui rend l'écran
 * tenable là où un compte porte 741 besoins, et c'est aussi ce qui garde les deux sections et
 * les boutons de l'assistant atteignables sans traverser mille lignes.
 *
 * Son en-tête est **collant** (`position: sticky`) : sur une liste qu'on fait défiler, une
 * colonne dont on ne voit plus le titre est une colonne qu'on lit de travers.
 *
 * Les valeurs sont reprises telles quelles plutôt que réinventées, et elles vivent en
 * utilitaires de mise en page : ce bloc n'a ni couleur ni graisse à lui, tout vient des jetons
 * du design system.
 *
 * ── CE QUI N'EST PLUS ICI, ET POURQUOI
 *
 * **Le champ de recherche et le compteur ont quitté ce composant.** Ils y étaient, donc ils
 * n'existaient que tant qu'une liste existait : une requête en vol rend `data` indéfini,
 * `AttachmentRow` cessait de rendre ce composant, et le champ disparaissait A CHAQUE TOUCHE
 * avec son contenu et le curseur. On tapait quatre lettres, il en restait deux.
 *
 * La règle : **un contrôle qui filtre ne se monte jamais sous ce qu'il filtre.** Ce composant
 * ne rend donc que la liste, et il peut disparaître sans emporter la recherche.
 */
const ActivityPicker = ({ activities, needIds, serviceIds, onToggleNeed, onToggleService, onToggleMarket }: Props) => {
    const checkedNeeds = new Set(needIds);
    const checkedServices = new Set(serviceIds);

    /**
     * La ligne, et ce qu'elle dit d'elle-même.
     *
     * **Le badge « désigné par la source » est sur la LIGNE et non dans un compteur global**,
     * et c'est ce qui remplace le décompte d'affaires de la version par marché : une case
     * cochée sans raison à côté est un périmètre accordé par personne. Ici la raison est la
     * ligne elle-même.
     *
     * Le statut part **à droite**, dans la colonne de méta de la maquette (`margin-left:auto`)
     * : posé contre le nom il se lit comme une partie du nom, ce qui est faux et gênant sur
     * des libellés déjà longs.
     *
     * Le nom peut être nul, et il s'écrit alors : une case sans libellé se lit comme une ligne
     * cassée, et l'opérateur ne saurait pas ce qu'il coche.
     */
    const row = (option: Option, checked: boolean, toggle: (id: string, checked: boolean) => void, child = false) => (
        <li
            key={option.id}
            className={`flex items-center gap-2.5 border-b border-border-norm px-2.5 py-1.5 last:border-b-0 ${
                child ? 'pl-8' : ''
            }`}
        >
            <Checkbox
                id={`activity-${option.id}`}
                checked={checked}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    toggle(option.id as string, event.target.checked)
                }
            >
                <span className="flex flex-wrap items-center gap-2">
                    <span>{option.name ?? c('Onboarding activity').t`Unnamed`}</span>
                    {option.linkedToContact && (
                        <Badge category="info" size="small">
                            {c('Onboarding activity').t`Named by the source`}
                        </Badge>
                    )}
                </span>
            </Checkbox>
            {option.displayStatus && (
                <span className="ml-auto shrink-0 text-xs text-text-600">{option.displayStatus}</span>
            )}
        </li>
    );

    /**
     * Le bloc borné, avec son en-tête collant.
     *
     * `max-h-[340px]` et `overflow-auto` sont les valeurs de la maquette. Le bloc défile dans
     * lui-même : sans cette borne, un compte à 741 besoins pousse les deux sections et les
     * boutons de l'assistant à mille lignes plus bas.
     */
    const bloc = (entete: React.ReactNode, lignes: React.ReactNode) => (
        <div className="max-h-[340px] overflow-auto rounded-lg border border-border-norm">
            {entete}
            <ul className="flex flex-col">{lignes}</ul>
        </div>
    );

    const entete = (gauche: string, droite: string) => (
        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border-norm bg-background-weak px-2.5 py-2 text-xs font-semibold uppercase tracking-wider text-text-600">
            <span className="grow">{gauche}</span>
            <span>{droite}</span>
        </div>
    );

    return (
        <>
            {/*
                Ce que le plafond a laissé dehors, dit en toutes lettres. Un sélecteur tronqué
                en silence fait croire que le compte n'a que ce qu'on voit, et l'opérateur coche
                alors un périmètre qu'il croit complet. Zéro sur tous les comptes connus, le
                plus gros portant 741 besoins.
            */}
            {(activities.omittedNeeds > 0 || activities.omittedServices > 0) && (
                <p className="mt-2 text-xs text-danger-700" role="alert">
                    {c('Onboarding activity')
                        .t`This account carries more activities than this list shows. Narrow the search to reach the ones missing.`}
                </p>
            )}

            <h4 className="mt-4 mb-2 text-sm font-semibold text-text-900">{c('Onboarding activity').t`Needs`}</h4>
            {activities.needs.length === 0
                ? bloc(
                      entete(c('Onboarding activity').t`Need`, c('Onboarding activity').t`Status`),
                      <li className="px-2.5 py-2 text-xs text-text-600">
                          {c('Onboarding activity').t`No need matches.`}
                      </li>
                  )
                : bloc(
                      entete(c('Onboarding activity').t`Need`, c('Onboarding activity').t`Status`),
                      activities.needs.map((option) => row(option, checkedNeeds.has(option.id as string), onToggleNeed))
                  )}

            <h4 className="mt-5 mb-2 text-sm font-semibold text-text-900">
                {c('Onboarding activity').t`Services, grouped by market`}
            </h4>
            {activities.marketGroups.length === 0 ? (
                <p className="text-xs text-text-600">{c('Onboarding activity').t`No service matches.`}</p>
            ) : (
                activities.marketGroups.map((group) => {
                    const ids = group.services.map((s) => s.id as string);
                    const checkedCount = ids.filter((id) => checkedServices.has(id)).length;
                    return (
                        <div key={group.marketId ?? 'no-market'} className="mb-2">
                            {/*
                                CHAQUE MARCHÉ EST SON PROPRE BLOC BORNÉ, comme dans la maquette :
                                un compte peut porter un marché à trois cents prestations, et un
                                seul bloc pour tous les marchés ferait défiler l'un pour
                                atteindre l'autre.

                                Le cadre est DANS le bloc et dans son en-tête collant : il reste
                                visible pendant qu'on fait défiler ses prestations, sinon on
                                coche des lignes sans savoir sous quel marché.
                            */}
                            {bloc(
                                <div className="sticky top-0 z-10 flex items-center gap-2.5 border-b border-border-norm bg-background-weak px-2.5 py-2">
                                    <Checkbox
                                        id={`market-${group.marketId ?? 'none'}`}
                                        checked={ids.length > 0 && checkedCount === ids.length}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                            onToggleMarket(ids, event.target.checked)
                                        }
                                    >
                                        <span className="flex flex-wrap items-center gap-2 font-semibold">
                                            {/* Le marché sans nom et le SANS marché sont deux
                                                phrases différentes : la première dit qu'un cadre
                                                existe et n'a pas d'intitulé, la seconde qu'il
                                                n'y en a aucun. Les confondre accuserait le
                                                référentiel d'un trou qu'il n'a pas. */}
                                            {group.marketId === null
                                                ? c('Onboarding activity').t`Without a framework agreement`
                                                : (group.marketName ??
                                                  c('Onboarding activity').t`Market without a name`)}
                                        </span>
                                    </Checkbox>
                                    {/*
                                        LA PART COCHÉE, DITE PLUTÔT QUE DESSINÉE. Le `Checkbox`
                                        du design system n'a pas d'état intermédiaire, et
                                        l'ajouter serait un lot à lui : sans lui, une case
                                        décochée au-dessus de prestations cochées se lirait
                                        « rien de ce groupe », ce qui est faux. La fraction en dit
                                        même plus qu'un tiret gris.
                                    */}
                                    <span className="ml-auto shrink-0 text-xs text-text-600">
                                        {checkedCount > 0 && checkedCount < ids.length
                                            ? c('Onboarding activity').t`${checkedCount} of ${ids.length}`
                                            : c('Onboarding activity').t`${ids.length} services`}
                                    </span>
                                </div>,
                                group.services.map((option) =>
                                    row(option, checkedServices.has(option.id as string), onToggleService, true)
                                )
                            )}
                        </div>
                    );
                })
            )}
        </>
    );
};

export default ActivityPicker;
